import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Loader2, AlertTriangle, Check, Bookmark, ChevronLeft, ChevronRight } from 'lucide-react'
import { getBookFile } from '../../../lib/readingFileStore'

// In-app EPUB reader for on-device book files. epubjs is dynamic-imported
// so it (and its jszip dependency) stay in a lazy chunk, off the main
// bundle. The book renders into an epubjs-managed iframe, so page turns
// use a visible bottom bar (taps inside the iframe don't bubble out to
// overlay zones the way the PDF canvas does).
//
// Progress is stored as a 0-100 percentage on reading_progress (shared
// with the PDF + text readers), derived from epubjs CFI locations.
export function EpubReader({ book, savedPosition, saveProgress, t }) {
  const navigate = useNavigate()

  const [status, setStatus] = useState('loading') // loading | missing | error | ready
  const [percent, setPercent] = useState(null)
  const [errMsg, setErrMsg] = useState('')
  const [savedFlash, setSavedFlash] = useState(false)

  const viewerRef = useRef(null)
  const bookRef = useRef(null)
  const renditionRef = useRef(null)
  const restoredRef = useRef(false)
  const locationsReadyRef = useRef(false)
  const saveTimerRef = useRef(null)
  // Latest synced % (for a cross-device resume fallback when this device
  // has no local CFI yet). The exact per-device spot lives in localStorage.
  const progressRef = useRef(0)
  useEffect(() => { progressRef.current = savedPosition || 0 }, [savedPosition])
  const cfiKey = `phoenix:epub-cfi:${book.id}`

  useEffect(() => {
    let alive = true
    setStatus('loading')
    ;(async () => {
      const rec = await getBookFile(book.id)
      if (!alive) return
      if (!rec?.blob) { setStatus('missing'); return }
      try {
        const ePubMod = await import('epubjs')
        const ePub = ePubMod.default || ePubMod
        const data = await rec.blob.arrayBuffer()
        if (!alive) return
        const epub = ePub(data)
        bookRef.current = epub
        const rendition = epub.renderTo(viewerRef.current, {
          width: '100%', height: '100%', flow: 'paginated', spread: 'none',
          allowScriptedContent: false,
        })
        renditionRef.current = rendition
        // Theme the EPUB's own markup to the app's light/dark mode — the
        // book ships its own (usually black-on-white) CSS, which is
        // invisible in dark mode. !important to beat the book's styles.
        const dark = document.documentElement.classList.contains('dark')
        const fg = dark ? '#e5e7eb' : '#1f2937'
        const bg = dark ? '#0b0b0f' : '#ffffff'
        rendition.themes.register('app', {
          body: { background: bg, color: fg, 'padding': '8px 6px !important', 'line-height': '1.7 !important' },
          'p, div, span, li, a, h1, h2, h3, h4, h5, h6, blockquote, td, th, em, strong, b, i': { color: `${fg} !important` },
          a: { color: `${dark ? '#fda4af' : '#be123c'} !important` },
          img: { 'max-width': '100% !important', height: 'auto !important' },
        })
        rendition.themes.select('app')

        // Swipe/tap navigation is handled by a transparent overlay in our
        // own DOM (see the JSX), because touch events inside the epubjs
        // iframe don't reliably reach listeners we attach there.

        // Persist the exact spot (CFI) on every turn — available
        // immediately, unlike percentage which needs generated locations.
        rendition.on('relocated', (location) => {
          if (!alive) return
          const cfi = location?.start?.cfi
          if (cfi) { try { localStorage.setItem(cfiKey, cfi) } catch { /* ignore */ } }
          const p = location?.start?.percentage
          if (typeof p === 'number') {
            setPercent(Math.round(p * 100))
            if (locationsReadyRef.current) {
              if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
              saveTimerRef.current = setTimeout(() => saveProgress(p * 100), 700)
            }
          }
        })

        // Resume exactly where we left off (per-device CFI), else page 1.
        // A stale/invalid CFI must not break the reader — fall back.
        let savedCfi = null
        try { savedCfi = localStorage.getItem(cfiKey) } catch { /* ignore */ }
        try { await rendition.display(savedCfi || undefined) }
        catch { await rendition.display() }
        if (!alive) return
        restoredRef.current = true
        setStatus('ready')

        // Locations enable the % readout + a cross-device resume fallback
        // (this device has no local CFI but the row has a synced %).
        await epub.ready
        await epub.locations.generate(1600)
        if (!alive) return
        locationsReadyRef.current = true
        if (!savedCfi && progressRef.current > 0.5) {
          const cfi = epub.locations.cfiFromPercentage(progressRef.current / 100)
          if (cfi) rendition.display(cfi)
        }
        // Percentage is only meaningful once locations exist; no page
        // turn has fired since, so recompute it for the current spot
        // (otherwise the readout is stuck at 0%).
        try {
          const cur = rendition.currentLocation()
          const cfi = cur?.start?.cfi
          if (cfi) {
            const p = epub.locations.percentageFromCfi(cfi)
            if (typeof p === 'number') setPercent(Math.round(p * 100))
          }
        } catch { /* ignore */ }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[EpubReader]', e)
        if (alive) { setErrMsg(e?.message ? String(e.message) : String(e)); setStatus('error') }
      }
    })()
    return () => {
      alive = false
      try { renditionRef.current?.destroy?.() } catch { /* ignore */ }
      try { bookRef.current?.destroy?.() } catch { /* ignore */ }
      renditionRef.current = null
      bookRef.current = null
    }
  }, [book.id, saveProgress, cfiKey])

  const prev = useCallback(() => { renditionRef.current?.prev?.() }, [])
  const next = useCallback(() => { renditionRef.current?.next?.() }, [])

  // Swipe + tap navigation via a transparent overlay in our own DOM
  // (iframe touches don't reach us). A horizontal swipe turns the page;
  // a plain tap on the left/right third also turns it.
  const touchRef = useRef({ x: 0, y: 0, t: 0 })
  const onTouchStart = (e) => {
    const tt = e.changedTouches[0]
    touchRef.current = { x: tt.clientX, y: tt.clientY, t: Date.now() }
  }
  const onTouchEnd = (e) => {
    const tt = e.changedTouches[0]
    const { x, y, t: t0 } = touchRef.current
    const dx = tt.clientX - x, dy = tt.clientY - y, dt = Date.now() - t0
    if (Math.abs(dx) > 35 && Math.abs(dx) > Math.abs(dy) * 1.2 && dt < 800) {
      if (dx < 0) next(); else prev()
      return
    }
    if (Math.abs(dx) < 12 && Math.abs(dy) < 12 && dt < 400) {
      const rect = e.currentTarget.getBoundingClientRect()
      const px = tt.clientX - rect.left
      if (px < rect.width * 0.33) prev()
      else if (px > rect.width * 0.67) next()
    }
  }

  // Explicitly bookmark the current spot (it also auto-saves on each
  // turn; this is the reassuring manual save + visual confirmation).
  const onSaveSpot = useCallback(() => {
    const loc = renditionRef.current?.currentLocation?.()
    const cfi = loc?.start?.cfi
    if (cfi) { try { localStorage.setItem(cfiKey, cfi) } catch { /* ignore */ } }
    const p = loc?.start?.percentage
    if (typeof p === 'number') saveProgress(p * 100)
    setSavedFlash(true)
    setTimeout(() => setSavedFlash(false), 1500)
  }, [cfiKey, saveProgress])

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-card">
      <header className="flex items-center justify-between border-b border-neutral-200 bg-card px-4 py-2 dark:border-neutral-800" style={{ paddingTop: 'calc(env(safe-area-inset-top) + 0.5rem)' }}>
        <button
          type="button"
          onClick={() => navigate(`/reading/${book.id}`)}
          className="grid h-9 w-9 place-items-center rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800"
          aria-label={t('reading.back')}
        >
          <ArrowLeft className="h-5 w-5 rtl:-scale-x-100" />
        </button>
        <div className="min-w-0 flex-1 px-2 text-center">
          <p dir="auto" className="line-clamp-1 text-sm font-medium">{book.title}</p>
        </div>
        <button
          type="button"
          onClick={onSaveSpot}
          className="grid h-9 w-9 place-items-center rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800"
          aria-label={t('reading.saveSpot')}
        >
          {savedFlash ? <Check className="h-5 w-5 text-emerald-500" /> : <Bookmark className="h-5 w-5" />}
        </button>
      </header>

      <div className="relative flex-1 overflow-hidden">
        {/* Viewer host is always mounted at full size so epubjs has a
            correctly-measured target the moment it renders. Status
            screens sit on top as absolute overlays. */}
        <div ref={viewerRef} className="h-full w-full" />

        {/* Transparent gesture layer over the iframe — catches swipe/tap
            in our DOM since the iframe's own touches don't reach us. */}
        {status === 'ready' && (
          <div className="absolute inset-0 z-10" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} />
        )}

        {status === 'loading' && (
          <div className="absolute inset-0 flex items-center justify-center bg-card"><Loader2 className="h-6 w-6 animate-spin opacity-60" /></div>
        )}
        {status === 'missing' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-card p-8 text-center">
            <AlertTriangle className="mb-3 h-7 w-7 text-amber-500" />
            <p dir="auto" className="max-w-xs text-sm opacity-80">{t('reading.file.missing')}</p>
            <button type="button" onClick={() => navigate(`/reading/${book.id}`)} className="mt-4 rounded-full bg-rose-500 px-5 py-2 text-sm font-medium text-white">
              {t('reading.back')}
            </button>
          </div>
        )}
        {status === 'error' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-card px-6 text-center">
            <p dir="auto" className="text-sm text-rose-500">{t('reading.readerLoadFailed')}</p>
            {errMsg && <p dir="ltr" className="mt-2 max-w-xs break-words text-[11px] opacity-60">{errMsg}</p>}
            <button type="button" onClick={() => navigate(`/reading/${book.id}`)} className="mt-4 rounded-full bg-rose-500 px-5 py-2 text-sm font-medium text-white">
              {t('reading.back')}
            </button>
          </div>
        )}
      </div>

      {status === 'ready' && (
        <div className="flex items-center justify-between gap-3 border-t border-neutral-200 px-4 py-2 dark:border-neutral-800" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 0.5rem)' }}>
          <button type="button" onClick={prev} aria-label={t('reading.previousPage')} className="grid h-9 w-9 place-items-center rounded-full bg-neutral-100 dark:bg-neutral-800">
            <ChevronLeft className="h-5 w-5 rtl:-scale-x-100" />
          </button>
          <span dir="ltr" className="text-[11px] tabular-nums opacity-60">{percent != null ? `${percent}%` : '…'}</span>
          <button type="button" onClick={next} aria-label={t('reading.nextPage')} className="grid h-9 w-9 place-items-center rounded-full bg-neutral-100 dark:bg-neutral-800">
            <ChevronRight className="h-5 w-5 rtl:-scale-x-100" />
          </button>
        </div>
      )}
    </div>
  )
}

export default EpubReader
